import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCardIcon,
  BankIcon,
  ArrowSquareOutIcon,
  ArrowsClockwiseIcon,
  PaperPlaneTiltIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";

import {
  ErrorBanner,
  PrimaryButton,
  SecondaryButton,
} from "../crm/Form";
import BillingStatusBadge from "./BillingStatusBadge";
import CopyButton from "../CopyButton";
import api, { formatApiError } from "../../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Compact "x minutes ago" / "yesterday at hh:mm" formatter. */
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.round((now - then) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)} hr ago`;
  if (sec < 604800) return `${Math.round(sec / 86400)} day ago`;
  return fmt(iso);
}

function amountDisplay(sub) {
  if (!sub) return "—";
  const dollars = Number(sub.amount_cents || 0) / 100;
  const cadence = sub.billing_cadence || "monthly";
  const suffix = {
    monthly: "/month",
    quarterly: "/quarter",
    annual: "/year",
    yearly: "/year",
  }[cadence] || `/${cadence}`;
  return `$${dollars.toFixed(2)}${suffix}`;
}

/**
 * Composite billing card on SubscriptionDetail.
 * Office-only (caller must hide for techs).
 */
export default function BillingPanel({ subscription, onChange }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [customer, setCustomer] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(
        `/subscriptions/${subscription.id}/billing`
      );
      setInfo(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // Phase 7.6 — fetch the linked customer so the confirmation panel can
  // show name/email and decide whether to display "Email + SMS" delivery.
  const loadCustomer = async () => {
    if (!subscription.customer_id) return;
    try {
      const { data } = await api.get(`/customers/${subscription.customer_id}`);
      setCustomer(data);
    } catch {
      // Customer fetch is best-effort — confirmation panel falls back to
      // the denormalized name on the subscription doc.
    }
  };

  useEffect(() => {
    load();
    loadCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription.id]);

  const startCheckout = async () => {
    setBusy(true);
    setErr("");
    try {
      // Phase 7.6 — stay on this page so the admin can hand the URL to
      // the customer via "Send to customer". Existing endpoint persists
      // the URL on the subscription doc; we just refresh and re-render.
      await api.post(
        `/subscriptions/${subscription.id}/billing/checkout-session`,
        { allow_ach: true }
      );
      toast.success("Checkout link ready — send it to the customer.");
      if (onChange) await onChange();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/subscriptions/${subscription.id}/billing/portal-session`
      );
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/subscriptions/${subscription.id}/billing/sync`);
      toast.success("Synced from Stripe");
      await load();
      if (onChange) onChange();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendPaymentLink = async () => {
    setSending(true);
    setErr("");
    try {
      await api.post(
        `/billing/subscriptions/${subscription.id}/send-payment-link`
      );
      const who = customer?.name || subscription.customer_name || "customer";
      toast.success(`Payment link sent to ${who}`);
      setConfirmOpen(false);
      if (onChange) await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
      setErr(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!info) {
    return (
      <section
        data-testid="billing-panel"
        className="rounded-md border border-slate-200 bg-white p-5"
      >
        <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
          Billing
        </div>
        <ErrorBanner message={err} />
        <div className="text-xs text-slate-500">Loading…</div>
      </section>
    );
  }

  const isStripe = info.billing_mode === "stripe";
  const checkoutUrl = subscription.stripe_checkout_url;
  // Show the Send-to-customer block whenever a checkout URL has been
  // created and the subscription isn't yet billing successfully.
  const showSendBlock =
    !!checkoutUrl &&
    info.billing_status !== "current" &&
    subscription.status !== "canceled";

  return (
    <section
      data-testid="billing-panel"
      className="rounded-md border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading text-sm font-semibold text-slate-900">
            Billing
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {isStripe ? "Stripe-managed" : "Manual record-keeping"}
          </div>
        </div>
        <BillingStatusBadge status={info.billing_status} />
      </div>

      <ErrorBanner message={err} />

      {isStripe && (
        <dl
          data-testid="billing-stripe-meta"
          className="text-xs text-slate-700 space-y-1.5 mb-4 pb-4 border-b border-slate-100"
        >
          <div>
            <span className="text-slate-500">Last payment:</span>{" "}
            <span className="font-data">{fmt(info.last_payment_at)}</span>
          </div>
          <div>
            <span className="text-slate-500">Last invoice:</span>{" "}
            <span className="font-data">{info.last_invoice_id || "—"}</span>{" "}
            {info.last_invoice_status && (
              <span
                className={
                  info.last_invoice_status === "processing"
                    ? "text-amber-700 font-semibold"
                    : "text-slate-500"
                }
              >
                ({info.last_invoice_status === "processing"
                  ? "processing · ACH in-flight"
                  : info.last_invoice_status})
              </span>
            )}
          </div>
          <div data-testid="billing-method-row">
            <span className="text-slate-500">
              {info.payment_method_type === "us_bank_account"
                ? "Bank account on file:"
                : "Card on file:"}
            </span>{" "}
            <span
              className={
                info.has_active_payment_method
                  ? "text-emerald-700 font-semibold"
                  : "text-amber-800 font-semibold"
              }
            >
              {info.has_active_payment_method
                ? info.payment_method_last4
                  ? `Yes · ••${info.payment_method_last4}`
                  : "Yes"
                : "No"}
            </span>
            {info.bank_account_verification_status === "pending_verification" && (
              <span className="ml-2 inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                Verification pending
              </span>
            )}
            {info.bank_account_verification_status === "verification_failed" && (
              <span className="ml-2 inline-flex items-center rounded-md border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-800">
                Verification failed
              </span>
            )}
          </div>
          {info.cancel_at_period_end && (
            <div className="text-rose-700">Will cancel at period end</div>
          )}
        </dl>
      )}

      <div className="flex flex-wrap gap-2">
        {!isStripe && subscription.status !== "canceled" && (
          <PrimaryButton
            type="button"
            onClick={startCheckout}
            disabled={busy}
            data-testid="billing-start-checkout"
          >
            <CreditCardIcon size={14} weight="duotone" />
            <BankIcon size={14} weight="duotone" className="-ml-1" />
            {busy
              ? "Starting…"
              : checkoutUrl
                ? "Regenerate checkout link"
                : "Set up automatic billing"}
          </PrimaryButton>
        )}
        {isStripe && (
          <>
            <SecondaryButton
              type="button"
              onClick={openPortal}
              disabled={busy}
              data-testid="billing-open-portal"
            >
              <ArrowSquareOutIcon size={14} weight="bold" />
              Update payment method
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={sync}
              disabled={busy}
              data-testid="billing-sync"
            >
              <ArrowsClockwiseIcon size={14} weight="bold" />
              Sync from Stripe
            </SecondaryButton>
          </>
        )}
      </div>

      {/* Phase 7.6 — Send Payment Link to Customer */}
      {showSendBlock && (
        <div
          data-testid="billing-send-block"
          className="mt-4 pt-4 border-t border-slate-100"
        >
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">
            Checkout link
          </div>
          <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 mb-3">
            <span
              className="flex-1 truncate font-data text-[11px] text-slate-700"
              data-testid="billing-checkout-url"
            >
              {checkoutUrl}
            </span>
            <CopyButton
              value={checkoutUrl}
              label="Checkout URL"
              testId="billing-copy-checkout-url"
            />
          </div>

          {!confirmOpen && (
            <div className="flex items-center gap-3">
              <PrimaryButton
                type="button"
                onClick={() => setConfirmOpen(true)}
                data-testid="billing-send-link-btn"
              >
                <PaperPlaneTiltIcon size={14} weight="duotone" />
                Send to customer
              </PrimaryButton>
              {subscription.payment_link_last_sent_at &&
                (subscription.payment_link_completed_at ? (
                  <span
                    className="text-[11px] text-emerald-700 inline-flex items-center gap-1 font-medium"
                    data-testid="billing-link-completed"
                  >
                    <CheckCircleIcon
                      size={12}
                      weight="fill"
                      className="text-emerald-500"
                    />
                    Customer completed payment ✓{" "}
                    {relativeTime(subscription.payment_link_completed_at)}
                  </span>
                ) : (
                  <span
                    className="text-[11px] text-slate-500 inline-flex items-center gap-1"
                    data-testid="billing-link-last-sent"
                  >
                    <CheckCircleIcon
                      size={12}
                      weight="fill"
                      className="text-emerald-500"
                    />
                    Last sent {relativeTime(subscription.payment_link_last_sent_at)}
                  </span>
                ))}
            </div>
          )}

          {confirmOpen && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50/60 p-3"
              data-testid="billing-send-confirm"
            >
              <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
                Send payment link to:
              </div>
              <dl className="text-xs text-slate-700 space-y-1 mb-3">
                <div>
                  <span className="text-slate-500">Customer:</span>{" "}
                  <span className="font-medium">
                    {customer?.name || subscription.customer_name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>{" "}
                  <span className="font-data">
                    {customer?.email || (
                      <span className="text-red-700">not on file</span>
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Plan:</span>{" "}
                  <span className="font-medium">
                    {subscription.plan_name} · {amountDisplay(subscription)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Delivery:</span>{" "}
                  <span className="font-medium">
                    {customer?.sms_consent && customer?.phone
                      ? "Email + SMS"
                      : "Email only"}
                  </span>
                </div>
              </dl>
              <div className="flex gap-2">
                <PrimaryButton
                  type="button"
                  onClick={sendPaymentLink}
                  disabled={sending || !customer?.email}
                  data-testid="billing-send-link-confirm"
                >
                  <PaperPlaneTiltIcon size={14} weight="duotone" />
                  {sending ? "Sending…" : "Confirm — Send link"}
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={sending}
                  data-testid="billing-send-link-cancel"
                >
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          )}
        </div>
      )}

      {!isStripe && (
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          Click <strong>Set up automatic billing</strong> to send the customer a
          Stripe Checkout link. Customer can pay by <strong>card</strong> or
          <strong> bank account (ACH)</strong>. ACH takes roughly 5 business
          days for the first payment to clear — we'll mark the subscription{" "}
          <em>pending</em> until verification completes.
        </div>
      )}
    </section>
  );
}
